import {
	IconBriefcase,
	IconDownload,
	IconMail,
	IconShieldLock,
	IconStar,
	IconTicket,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { cn } from "@/lib/utils";
import { MARKETING_SECTION_ANIM_BASE } from "@/utils/animation";
import { seo } from "@/utils/seo";

const faqs = [
	{
		question: "Is it safe to use a disposable email for sign-ups?",
		answer:
			"For sign-ups where you are evaluating a product or downloading a resource — yes. The risk is that you lose access to the account when the inbox expires. If you decide to keep using the service, you will want to update to a real email address before the inbox is gone. Do not use a disposable address for any account you plan to keep long-term, or for anything that requires account recovery.",
	},
	{
		question: "Will the sign-up form accept an ineedatest.email address?",
		answer:
			"Most will. ineedatest.email uses multiple receiving domains, and less-known domains pass more sign-up form validators than well-known disposable services like Mailinator or Temp Mail. Some forms use aggressive disposable-email detection — if one domain is rejected, try a different one from the domain picker. No disposable email service is universally accepted.",
	},
	{
		question: "What happens to the account when my inbox expires?",
		answer:
			"The inbox stops receiving mail and is deleted. The account at the third-party service still exists — it is just unreachable by email. If you need a password reset or email-based 2FA later, you will not be able to receive it. For any trial where you might want to continue past the evaluation period, update your email address in the account settings before your inbox expires.",
	},
	{
		question: "Can I use the same disposable address for multiple sign-ups?",
		answer:
			"Yes. You can use the same local part and domain combination for multiple services — each sign-up email goes to the same inbox. The practical risk is that if one of those services sends a lot of mail, it becomes harder to find messages from others. For cleaner testing, use a different local part per service.",
	},
	{
		question: "How long should I set the expiry for a sign-up?",
		answer:
			"Long enough to complete the sign-up flow, click the confirmation link, and do your evaluation. For most SaaS trials that is a few days. For a one-time download or newsletter confirmation, 30 minutes is plenty. If you are doing a full 14-day trial, set the expiry to match — or update your email to a real address once you decide you want to continue.",
	},
];

const scenarios = [
	{
		icon: IconBriefcase,
		title: "SaaS trials and free tiers",
		body: "Every SaaS product with a free tier eventually sends a nurture sequence. Use a disposable address for the initial trial. If the product earns a real relationship, update the account email. If it does not, the inbox expires and you never hear from them again.",
	},
	{
		icon: IconDownload,
		title: "Gated content and lead magnets",
		body: "White papers, ebooks, templates, and reports that sit behind an email gate. Enter a disposable address, download the resource, and stay out of the marketing sequence that follows. The download link arrives in the inbox; the follow-up sequence goes nowhere.",
	},
	{
		icon: IconMail,
		title: "Newsletters you want to sample once",
		body: "Sign up for a newsletter you are curious about without committing your real inbox. Read a few issues from the disposable inbox and decide if it is worth a real subscription. If it is, re-subscribe with your real address. If not, the inbox expires.",
	},
	{
		icon: IconStar,
		title: "Review sites and comparison platforms",
		body: "Many review aggregators, comparison engines, and deal sites require an email to unlock full content or post a review. Use a disposable address to access what you need without adding to yet another marketing list.",
	},
	{
		icon: IconTicket,
		title: "Event registrations and webinars",
		body: "Webinar platforms, conference registration systems, and community sign-ups often require an email they will use for months of follow-up. A disposable address gets you the confirmation and reminder without the sustained outreach.",
	},
	{
		icon: IconShieldLock,
		title: "Vendors you are not sure about yet",
		body: "When evaluating a new vendor, contractor platform, or marketplace — especially one with unclear data practices — a disposable address keeps your real inbox out of their database until you decide they have earned it.",
	},
];

export const Route = createFileRoute("/_marketing/for/sign-up")({
	component: RouteComponent,
	head: () =>
		seo({
			title: "Temporary email for sign-ups — ineedatest.email",
			description:
				"Use a disposable inbox for trials, downloads, and sign-ups you are not sure about. No account required. Expires when you say.",
			keywords:
				"temporary email for sign up, disposable email sign up, throwaway email sign up, fake email for sign up, temp email registration",
			type: "FAQPage",
			faqItems: faqs,
		}),
});

export function RouteComponent() {
	return (
		<MarketingShell>
			{/* Hero */}
			<section className="pb-12 pt-20 sm:pb-16 sm:pt-28">
				<MarketingContainer>
					<nav className="mb-6 text-xs text-muted-foreground">
						<a href="/" className="hover:text-foreground">
							ineedatest.email
						</a>
						<span className="mx-2">/</span>
						<span>Temporary email for sign-ups</span>
					</nav>
					<div className={cn("max-w-3xl", MARKETING_SECTION_ANIM_BASE)}>
						<p className="mb-3 text-sm font-medium uppercase tracking-wider text-primary">
							Sign-up protection
						</p>
						<h1 className="mb-4 text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
							Temporary email for sign-ups
						</h1>
						<p className="text-lg text-pretty text-muted-foreground sm:text-xl">
							Every sign-up form is a promise that your inbox will be respected.
							Most are not. Use a disposable address for trials, downloads, and
							services you are not sure about — keep your real inbox for the
							things that matter.
						</p>
					</div>
				</MarketingContainer>
			</section>

			{/* The problem */}
			<section className="border-t border-border/50 bg-muted/15 py-12 sm:py-16">
				<MarketingContainer>
					<div className="max-w-2xl">
						<p className="mb-2 text-sm font-medium uppercase tracking-wider text-primary">
							Why this matters
						</p>
						<h2 className="mb-4 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
							Your real email address is a long-term commitment
						</h2>
						<p className="mb-4 text-base text-pretty text-muted-foreground">
							Sign up for a SaaS trial with your real email and you are in their
							database indefinitely. Even after unsubscribing, your address sits
							in their CRM, gets included in data-broker sales, and shows up in
							breach datasets years later.
						</p>
						<p className="text-base text-pretty text-muted-foreground">
							A disposable address makes the sign-up genuinely temporary. You
							get the confirmation, click the link, use the product. When the
							inbox expires, the trail ends there.
						</p>
					</div>
				</MarketingContainer>
			</section>

			{/* Scenarios */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="When to use it"
						heading="Six sign-up scenarios where a disposable address makes sense"
						description="Not every sign-up deserves your real inbox. Here are the situations where a throwaway address is the right call."
						className="mb-10 sm:mb-12"
					/>
					<div className="grid gap-6 sm:gap-8 lg:grid-cols-2">
						{scenarios.map((item, index) => {
							const Icon = item.icon;
							return (
								<div
									key={item.title}
									className={cn(
										"flex gap-5 rounded-xl border border-border/80 bg-card/30 p-6 ring-1 ring-border/40 sm:p-8",
										MARKETING_SECTION_ANIM_BASE,
									)}
									style={{ animationDelay: `${index * 0.08}s` }}
								>
									<div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/40">
										<Icon className="size-5 text-primary" aria-hidden />
									</div>
									<div className="flex min-w-0 flex-col gap-2">
										<h3 className="text-base font-semibold tracking-tight text-foreground">
											{item.title}
										</h3>
										<p className="text-sm text-pretty text-muted-foreground">
											{item.body}
										</p>
									</div>
								</div>
							);
						})}
					</div>
				</MarketingContainer>
			</section>

			{/* How to use */}
			<section className="border-t border-border/50 bg-muted/15 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="How to use it"
						heading="Three steps for any sign-up"
						className="mb-10 sm:mb-12"
					/>
					<div className="flex flex-col gap-5">
						{[
							{
								step: "1",
								title: "Create an address",
								body: 'Open ineedatest.email. Type a local part (e.g. "acme-trial"), pick a domain, set an expiry that covers your evaluation period, and copy the address.',
							},
							{
								step: "2",
								title: "Use it for the sign-up",
								body: "Paste the address into the sign-up form. The confirmation email arrives in the disposable inbox. Click the confirmation link, complete sign-up, start your trial.",
							},
							{
								step: "3",
								title: "Decide what happens next",
								body: "If you want to keep the account: update the account email to your real address before the inbox expires. If you do not: do nothing. The inbox expires, the emails go nowhere, the relationship ends.",
							},
						].map((item) => (
							<div
								key={item.step}
								className="flex gap-5 rounded-xl border border-border/60 bg-card/30 p-6 ring-1 ring-border/30"
							>
								<div className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted/40 text-sm font-semibold text-primary">
									{item.step}
								</div>
								<div className="flex min-w-0 flex-col gap-1">
									<h3 className="text-base font-semibold text-foreground">
										{item.title}
									</h3>
									<p className="text-sm text-pretty text-muted-foreground">
										{item.body}
									</p>
								</div>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* FAQ */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<MarketingHeader
						headingAs="h2"
						align="left"
						title="FAQ"
						heading="Questions about using disposable email for sign-ups"
						className="mb-10 sm:mb-12"
					/>
					<div className="divide-y divide-border/50">
						{faqs.map((faq) => (
							<div key={faq.question} className="py-6 first:pt-0 last:pb-0">
								<h3 className="mb-2 text-base font-semibold text-foreground">
									{faq.question}
								</h3>
								<p className="text-sm text-pretty text-muted-foreground leading-relaxed">
									{faq.answer}
								</p>
							</div>
						))}
					</div>
				</MarketingContainer>
			</section>

			{/* CTA + related */}
			<section className="border-t border-border/50 py-16 sm:py-20">
				<MarketingContainer>
					<div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<p className="text-base font-semibold text-foreground">
								Keep your real inbox clean
							</p>
							<p className="mt-1 text-sm text-muted-foreground">
								No account required. Set an expiry, copy the address, use it.
							</p>
						</div>
						<a
							href="/"
							className="shrink-0 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
						>
							Get a disposable inbox
						</a>
					</div>

					<div className="mt-12 border-t border-border/50 pt-8">
						<p className="mb-3 text-sm text-muted-foreground">
							More on ineedatest.email:{" "}
							<a
								href="/features"
								className="text-primary underline-offset-4 hover:underline"
							>
								product features
							</a>
						</p>
						<p className="mb-4 text-sm font-medium text-muted-foreground">
							Other ways to use disposable email
						</p>
						<div className="flex flex-wrap gap-3">
							<a
								href="/for/testing"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								For email testing
							</a>
							<a
								href="/for/no-registration"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								No registration
							</a>
							<a
								href="/for/testing"
								className="rounded-full border border-border/60 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:text-primary transition-colors"
							>
								For OTP codes
							</a>
						</div>
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
